<template>
  <div class="editable-reference-field">
    <div v-if="isEditing" class="edit-mode">
      <BbTextarea
        variant="outlined"
        v-model="localValue"
        :rows="3"
        :auto-grow="true"
        :placeholder="t('common.title')"
      />
      <div class="edit-mode__actions">
        <BbBtn type="secondary" size="small" @click="cancelEdit">
          <TextBody i18n-key="common.cancel" />
        </BbBtn>
        <BbBtn type="primary" size="small" :is-disabled="!localValue.trim()" @click="confirmUpdate">
          <TextBody i18n-key="common.save" />
        </BbBtn>
      </div>
    </div>
    <div v-else class="view-mode">
      <div class="view-mode__value" :class="{ 'view-mode__value_ellipsis': ellipsis }">{{ value }}</div>
      <div v-if="editable" class="view-mode__actions">
        <BbTooltip
          v-if="showSecondaryAction && (!resettable || showDeleteWithReset)"
          v-bind="{ placement: 'top', offset: 4, isDisabled: false, content: t('common.delete') }"
        >
          <BbBtn
            class="delete-icon-btn"
            type="icon"
            size="small"
            icon="delete"
            icon-size="small"
            icon-type="outlined"
            height="36"
            width="40"
            no-background
            @click="$emit('delete')"
          />
        </BbTooltip>
        <BbTooltip
          v-if="showSecondaryAction && resettable"
          v-bind="{ placement: 'top', offset: 4, isDisabled: false, content: t('author.resetToOriginalAiReference') }"
        >
          <BbBtn
            class="reset-icon-btn"
            type="icon"
            size="small"
            icon="discard-changes"
            icon-size="small"
            icon-type="outlined"
            height="36"
            width="40"
            no-background
            @click="$emit('reset')"
          />
        </BbTooltip>
        <BbTooltip v-bind="{ placement: 'top', offset: 4, isDisabled: false, content: t('common.edit') }">
          <BbBtn
            type="icon"
            size="small"
            icon="edit"
            icon-size="small"
            icon-type="outlined"
            height="36"
            width="40"
            no-background
            @click="$emit('set-editable')"
          />
        </BbTooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import TextBody from '~/components/common/TextBody.vue';

export default {
  name: 'EditableReferenceField',
  components: { TextBody },
  emits: ['update', 'cancel', 'set-editable', 'delete', 'reset'],
  props: {
    value: {
      type: String,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    ellipsis: {
      type: Boolean,
      default: true,
    },
    resettable: {
      type: Boolean,
      default: false,
    },
    // When false, hides both the delete and reset actions, leaving only edit — for callers with
    // nothing meaningful to delete/reset (e.g. a value that hasn't been customized yet, so there's
    // no override to discard). Defaults to true so every existing caller keeps showing whichever
    // of delete/reset `resettable` already picks, unchanged.
    showSecondaryAction: {
      type: Boolean,
      default: true,
    },
    // Keeps delete next to reset instead of reset replacing it, for a customized value whose whole entry
    // can still be removed (a second-order source).
    showDeleteWithReset: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();

    const localValue = ref(props.value);

    watch(
      () => props.value,
      (newValue) => {
        localValue.value = newValue;
      },
    );

    const confirmUpdate = () => {
      emit('update', localValue.value);
    };

    const cancelEdit = () => {
      localValue.value = props.value; // Reset to original value
      emit('cancel');
    };

    return {
      localValue,
      confirmUpdate,
      cancelEdit,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.editable-reference-field {
  display: flex;
  align-items: center;
  gap: $bb-spacing-1;
  width: 100%;
}

.edit-mode {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-1;
  width: 100%;

  .bb-textarea {
    width: 100% !important;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: $bb-spacing-1;
  }
}

.view-mode {
  display: flex;
  gap: $bb-spacing-1;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  align-items: center;

  &__value {
    @include bb-regular-text-styles('md');

    @media (min-width: 1440px) {
      @include bb-regular-text-styles('default');
    }

    width: 100%;
    max-width: 100%;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.2px;
    padding: 8px 12px;
    border: 1px solid transparent;
    box-sizing: border-box;

    &_ellipsis {
      overflow: hidden;
      word-break: break-all;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  &__actions {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    gap: $bb-spacing-1;
    flex-shrink: 0;
    margin-left: 0;
  }

  .delete-icon-btn {
    border: 1px solid $bb-interface-5 !important;
    border-radius: 50%;

    :deep(.bb-icon) {
      color: $bb-interface-5 !important;
    }

    &:hover {
      background-color: $bb-interface-5 !important;

      :deep(.bb-icon) {
        color: $bb-neutral-0 !important;
      }
    }
  }

  .reset-icon-btn {
    border: 1px solid $bb-interface-5 !important;
    border-radius: 50%;

    :deep(.bb-icon) {
      color: $bb-interface-5 !important;
    }

    &:hover {
      background-color: $bb-interface-5 !important;

      :deep(.bb-icon) {
        color: $bb-neutral-0 !important;
      }
    }
  }
}
</style>
